// AIコンソール > 文書生成 — SEN249 docgenサービス設定（secretsは書き込み専用・再表示不可）
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, PlugZap, ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Flag = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
    ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-600'}`}>
    {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
  </span>
);

export default function DocGenSection() {
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [cfId, setCfId] = useState('');
  const [cfSecret, setCfSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const load = () => axios.get(`${API}/ai-docs/config`, { withCredentials: true })
    .then((r) => { setCfg(r.data); setBaseUrl(r.data.base_url || ''); setCfId(''); })
    .catch(() => toast.error('設定の取得に失敗しました'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { base_url: baseUrl };
      if (token.trim()) body.service_token = token.trim();
      if (cfId.trim()) body.cf_access_client_id = cfId.trim();
      if (cfSecret.trim()) body.cf_access_client_secret = cfSecret.trim();
      const r = await axios.put(`${API}/ai-docs/config`, body, { withCredentials: true });
      setCfg(r.data); setToken(''); setCfId(''); setCfSecret('');
      toast.success('保存しました（トークンは再表示されません）');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try { setTest((await axios.post(`${API}/ai-docs/config/test`, null, { withCredentials: true })).data); }
    catch (_e) { toast.error('接続テストに失敗しました'); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-4" data-testid="docgen-config-section">
      <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
        <h3 className="text-[14px] font-bold text-zinc-900 flex items-center gap-2">
          <FileText size={16} className="text-violet-600" /> AI文書生成サービス（SEN249）
        </h3>
        <p className="text-[11.5px] text-zinc-500">
          SEN249上のdocgenサービスに接続します。専用ホスト名（例: docgen.sensbey.link）を使用し、
          LM Studioのホスト名とは分けてください。トークンは保存後に再表示されません（置換のみ可能）。
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-zinc-600 block mb-1">サービスURL</label>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://docgen.sensbey.link"
              data-testid="docgen-cfg-url"
              className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-zinc-600 block mb-1">
              サービストークン {cfg?.has_token && <span className="text-emerald-600">（設定済み — 入力すると置換）</span>}
            </label>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder={cfg?.has_token ? '●●●●●●（置換する場合のみ入力）' : 'DOCGEN_SERVICE_TOKEN の値'}
              data-testid="docgen-cfg-token" autoComplete="new-password"
              className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 block mb-1">CF-Access Client ID（任意）</label>
            <input value={cfId} onChange={(e) => setCfId(e.target.value)}
              placeholder={cfg?.has_cf_access ? '設定済み' : '未使用なら空欄'} data-testid="docgen-cfg-cfid"
              className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-600 block mb-1">CF-Access Client Secret（任意）</label>
            <input type="password" value={cfSecret} onChange={(e) => setCfSecret(e.target.value)}
              placeholder={cfg?.has_cf_access ? '●●●●●●' : '未使用なら空欄'} data-testid="docgen-cfg-cfsecret" autoComplete="new-password"
              className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button type="button" onClick={save} disabled={saving} data-testid="docgen-cfg-save"
            className="rounded-lg bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 text-[12px] font-bold disabled:opacity-50">
            {saving ? '保存中…' : '保存'}
          </button>
          <button type="button" onClick={runTest} disabled={testing || !cfg?.configured} data-testid="docgen-cfg-test"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-4 py-2 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50">
            <PlugZap size={13} /> {testing ? 'テスト中…' : '接続テスト'}
          </button>
          {cfg && (
            <span className="ml-auto text-[11px] text-zinc-400 inline-flex items-center gap-1">
              <ShieldCheck size={12} /> {cfg.configured ? '設定済み' : '未設定'}
            </span>
          )}
        </div>
        {test && (
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="docgen-cfg-test-result">
            <Flag ok={test.reachable} label={test.reachable ? '到達可' : '到達不可'} />
            <Flag ok={test.authorized} label={test.authorized ? '認証OK' : '認証エラー'} />
            <Flag ok={test.renderer_ready} label={test.renderer_ready ? 'レンダラー準備OK' : 'レンダラー未準備'} />
            <Flag ok={test.llm_reachable} label={test.llm_reachable ? 'LLM接続OK' : 'LLM接続不可'} />
          </div>
        )}
      </div>
    </div>
  );
}
